import takeRight = require('lodash/takeRight');
import uniqueId = require('lodash/uniqueId');
import {IConsoleAction} from './actions';

import {
  RUNNING,
  OUTPUT_LIMIT,
  CONSOLE_CLEAR,
  CONSOLE_RESULT,
} from './constants';

import {
  WS_CLEAR,
  WS_STDOUT,
  WS_STDERR,
  WS_RESULT,
  WS_DISCONNECTED,
  WS_LANGUAGE,
} from '../web-socket/constants';

type ConsoleMessageType = 'out' | 'error' | 'system';

export interface IConsoleMessage {
  id: string;
  type: ConsoleMessageType;
  data: string;
}

export interface ITest {
  passed: boolean;
  actual: string;
  stdOut: string;
  stdError: string;
}

export interface ITestResult {
  failed: number;
  passed: number;
}

export interface ITestMeta {
  input: string;
  output: string;
}

export interface ITests extends ITestResult {
  wallTime: number;
  meta: Array<ITestMeta>;
  testResults: Array<ITest>;
  testErrors: Array<string>;
  showTests: boolean;
  hasErrorCompilation: boolean;
}

export interface IConsoleState {
  messages: Array<IConsoleMessage>;
  running: boolean;
  trimmed: number;
  tests: ITests;
  runTests: boolean;
}

const initTests = {
  failed: 0,
  passed: 0,
  meta: [],
  wallTime: 0,
  testResults: [],
  testErrors: [],
  showTests: false,
  hasErrorCompilation: false,
};

const initialState: IConsoleState = {
  running: false,
  messages: [],
  trimmed: 0,
  tests: initTests,
  runTests: false,
};

const isRunning = (message: IConsoleMessage): boolean => (
  message && message.data === RUNNING
);

const generateMessageId = () => uniqueId('message-');

const stdoutMessage = (data: string): IConsoleMessage => (
  {id: generateMessageId(), type: 'out', data}
);

const errorMessage = (data: string): IConsoleMessage => (
  {id: generateMessageId(), type: 'error', data}
);

const systemMessage = (data: string): IConsoleMessage => (
  {id: generateMessageId(), type: 'system', data}
);

const trimOutput = (messages: Array<IConsoleMessage>) => (
  messages && messages.length > OUTPUT_LIMIT
    ? takeRight(messages, OUTPUT_LIMIT)
    : messages
);

const trimmed = (trimmedCount: number, messagesCount: number) => (
  messagesCount > OUTPUT_LIMIT
    ? trimmedCount + (messagesCount - OUTPUT_LIMIT)
    : 0
);

const result = (state: IConsoleState, tests: ITests): IConsoleState => {
  let messages: Array<IConsoleMessage> = isRunning(state.messages[0])
    ? state.messages.slice(1)
    : state.messages;
  const messagesLength = messages ? messages.length : 0;

  const trimmedCount = trimmed(state.trimmed, messagesLength);

  messages = trimOutput(messages);
  messages = trimmedCount
    ? [systemMessage(`${trimmedCount} lines were trimmed`), ...messages]
    : messages;
  messages = [...messages, systemMessage('Finished executing code')];

  return {
    ...state,
    trimmed: trimmedCount,
    running: false,
    tests: {
      ...state.tests,
      ...tests,
      showTests: true,
    },
    messages,
  };
};

const clear = (state: IConsoleState, runTests: boolean = false): IConsoleState => (
  {
    ...state,
    messages: [stdoutMessage(RUNNING)],
    running: true,
    trimmed: 0,
    tests: initTests,
    runTests,
  }
);

const stdout = (state: IConsoleState, messages: Array<string>): IConsoleState => {
  if (!state.running) {
    state = clear(state);
  }

  return {
    ...state,
    trimmed: trimmed(state.trimmed, state.messages.length),
    messages: isRunning(state.messages[0])
      ? [...messages.map(stdoutMessage)]
      : [...trimOutput(state.messages), ...messages.map(stdoutMessage)],
  };
};

const stderr = (state: IConsoleState, errors: Array<string>): IConsoleState => {
  if (!state.running) {
    state = clear(state);
  }

  return {
    ...state,
    trimmed: trimmed(state.trimmed, state.messages.length),
    messages: isRunning(state.messages[0])
      ? [...errors.map(errorMessage)]
      : [...trimOutput(state.messages), ...errors.map(errorMessage)],
  };
};

const disconnected = (state: IConsoleState): IConsoleState => {
  if (state.running) {
    return {
      ...state,
      messages: [...state.messages, systemMessage('Code execution failed on our side, please try again!')],
    };
  }

  return state;
};

export default function consoleReducer(state: IConsoleState = initialState,
                                       action: IConsoleAction = undefined): IConsoleState {
  switch (action.type) {
    case CONSOLE_CLEAR:
    case WS_CLEAR:
      const {initial = false, runTests = false} = action.payload;
      return initial ? initialState : clear(state, runTests);

    case WS_STDOUT:
      return stdout(state, action.payload);

    case WS_STDERR:
      return stderr(state, action.payload);

    case WS_RESULT:
      return result(state, action.payload);

    case WS_DISCONNECTED:
      return disconnected(state);

    case CONSOLE_RESULT:
      const {
        stderr: errors,
        stdout: messages,
        result: tests,
      } = action.payload;

      return result(
        stdout(stderr(state, errors), messages),
        tests,
      );

    case WS_LANGUAGE:
      return initialState;

    default:
      return state;
  }
}
